import { FiMoreVertical } from 'react-icons/fi';

import { Banner } from '@/components/banner/banner';
import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { MetricConfig, MetricProvider } from '@/types/evals';

import { SectionBlock } from '../../_components/SectionBlock';
import { MetricConfigBlock } from './MetricConfigBlock';
import { MetricFooterRow } from './MetricFooterRow';
import { MetricMetaRow } from './MetricMetaRow';
import styles from './MetricVersionConfigurationTab.module.scss';

type Props = {
  metric: MetricProvider;
  config: MetricConfig;
  onNewVersion: () => void;
  onVersionChange: ( version: number ) => void;
  onDeprecateToggle: () => void;
  isDeprecating: boolean;
  isCreatingVersion: boolean;
};

export function MetricVersionConfigurationTab( {
  metric,
  config,
  onNewVersion,
  onVersionChange,
  onDeprecateToggle,
  isDeprecating,
  isCreatingVersion
}: Props ) {
  const versionLabel = config.isDeprecated
    ? `Version ${ config.version } (Deprecated)`
    : `Version ${ config.version }`;

  const description = metric.description?.trim();

  return (
    <div
      className={ styles.tab }
      data-deprecated={ config.isDeprecated ? 'true' : undefined }
    >
      <div className={ styles.topBar }>
        <div className={ styles.topBarLeft }>
          <span className={ styles.topBarHeading }>Configuration Properties for</span>
          <Menu
            variant="select"
            value={ versionLabel }
            className={ styles.versionSelector }
            data-testid="version-selector"
          >
            { metric.versions.map( ( v ) => (
              <MenuItem
                key={ `version-option-${ v.version }` }
                value={ `Version ${ v.version }` }
                onClick={ () => onVersionChange( v.version ) }
                data-testid={ `version-option-${ v.version }` }
              />
            ) ) }
          </Menu>
        </div>
        <div className={ styles.topBarRight }>
          <Button
            variant="primary"
            onClick={ onNewVersion }
            disabled={ isCreatingVersion }
            data-testid="new-metric-version-button"
          >
            { isCreatingVersion ? 'Creating...' : 'Version Metric' }
          </Button>
          <Menu
            variant="menu"
            icon={ <Icon icon={ FiMoreVertical } /> }
            data-testid="version-kebab"
          >
            <MenuItem
              value={ config.isDeprecated ? 'Undeprecate Version' : 'Deprecate Version' }
              onClick={ onDeprecateToggle }
              disabled={ isDeprecating }
              data-testid="version-deprecate-toggle"
            />
          </Menu>
        </div>
      </div>

      { config.isDeprecated && (
        <Banner variant="warning" data-testid="deprecated-banner">
          Version { config.version } of <strong>&ldquo;{ config.name }&rdquo;</strong> is deprecated
        </Banner>
      ) }

      <MetricMetaRow config={ config } />

      <SectionBlock title="Description" data-testid="metric-description-block">
        <p className={ description ? styles.body : styles.empty }>
          { description || 'No description provided' }
        </p>
      </SectionBlock>

      <MetricConfigBlock config={ config } />

      <MetricFooterRow config={ config } />
    </div>
  );
}
